// Portal usage tracking — how often each employee opens the portal and how long they actually
// use it, so low adoption can be spotted and nudged (requested by Max, 2026-08-03).
//
// Two deliberate design choices:
//  * A "visit" is one browser tab. The key lives in sessionStorage, so a reload counts as the
//    same visit but a new tab (or coming back tomorrow) counts as a new one.
//  * Time is only counted while the tab is actually VISIBLE. A tab left open over lunch or
//    overnight would otherwise report eight hours of "usage" that nobody spent.
//
// Mounted inside ProtectedRoute, so it never runs on the login page or the public ticket page.
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { BACKEND_URL } from './backendConfig';

const VISIT_KEY_STORAGE = 'hevn_portal_visit_key';
// 30s rather than a minute: someone who opens the portal, looks for 40 seconds and closes the tab
// should still show up as time spent. The unload flush below is not reliable enough on its own.
const BEAT_MS = 30 * 1000;

const newVisitKey = () => {
  const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `v${Date.now().toString(36)}-${rand}`.slice(0, 64);
};

const visitKeyForTab = () => {
  try {
    let key = window.sessionStorage.getItem(VISIT_KEY_STORAGE);
    if (!key) {
      key = newVisitKey();
      window.sessionStorage.setItem(VISIT_KEY_STORAGE, key);
    }
    return key;
  } catch {
    // Private mode / storage blocked: still track, just as a fresh visit each load.
    return newVisitKey();
  }
};

/**
 * @param {string} path current route, recorded so the report can show which screens get used
 * @param {boolean} enabled only beat once the user is known to be signed in — pinging before that
 *        would just collect 401s while the auth check is still in flight
 */
export const useUsageTracker = (path, enabled = true) => {
  const visitKey = useRef(null);
  const visibleSince = useRef(null);   // when the tab last became visible
  const carried = useRef(0);           // visible seconds not yet reported
  const pathRef = useRef(path);
  const pendingPageChange = useRef(true); // the first beat of a route counts as a page view

  useEffect(() => {
    if (pathRef.current !== path) {
      pathRef.current = path;
      pendingPageChange.current = true;
    }
  }, [path]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!visitKey.current) visitKey.current = visitKeyForTab();
    if (document.visibilityState === 'visible') visibleSince.current = Date.now();

    /** Visible seconds since the previous beat, and reset the counter. */
    const takeElapsed = () => {
      let seconds = carried.current;
      carried.current = 0;
      if (visibleSince.current) {
        seconds += Math.round((Date.now() - visibleSince.current) / 1000);
        visibleSince.current = Date.now();
      }
      return seconds;
    };

    const beat = (deltaSeconds) => {
      const pageChanged = pendingPageChange.current;
      pendingPageChange.current = false;
      // Telemetry must never interfere with the page: swallow every failure.
      axios.post(`${BACKEND_URL}/api/portal/usage/ping`, {
        visitKey: visitKey.current,
        deltaSeconds,
        path: pathRef.current,
        pageChanged,
      }).catch(() => {});
    };

    beat(0); // register the visit immediately, so a 10-second look still counts as one visit

    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible' && !carried.current) return;
      beat(takeElapsed());
    }, BEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        visibleSince.current = Date.now();
      } else if (visibleSince.current) {
        // Bank the time spent before the tab went away; report it on the next beat.
        carried.current += Math.round((Date.now() - visibleSince.current) / 1000);
        visibleSince.current = null;
        beat(takeElapsed());
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Closing the tab: one last beat so the final minutes are not lost. keepalive lets the
    // request outlive the page.
    const onHide = () => {
      const seconds = takeElapsed();
      if (!seconds) return;
      try {
        const body = JSON.stringify({
          visitKey: visitKey.current, deltaSeconds: seconds, path: pathRef.current, pageChanged: false,
        });
        fetch(`${BACKEND_URL}/api/portal/usage/ping`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          credentials: 'include',
          keepalive: true,
        }).catch(() => {});
      } catch { /* nothing useful to do while the page is unloading */ }
    };
    window.addEventListener('pagehide', onHide);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
    };
  }, [enabled]);
};

export default useUsageTracker;
